import { alphaSorter } from 'vet-bones/utils';

import type { DatabasesAction } from 'src/actions/databases/actionCreators';
import { DatabasesActionType } from 'src/actions/databases/actionTypes';
import { DatabaseProperty } from 'src/constants/databaseProperties';
import { UnknownAction } from 'src/constants/types';
import {
  connectionAsyncActions,
  connectionsSlice,
} from 'src/store/connections/connections.slice';
import {
  Database,
  DatabasesState,
  NamedGraph,
} from 'src/store/state/databases';
import { DEFAULT_MODEL } from 'src/templates/queries/iris';
import { NamedGraphBinding } from 'src/templates/queries/namedGraphs';
import { formatSPGByName } from 'src/utils/formatSPGByName';
import { getDefaultModelNamedGraphs } from 'src/utils/getDefaultModelNamedGraphs';
import { getNamedGraphsByModel } from 'src/utils/getNamedGraphsByModel';

const parseDatabaseInfo = (data: any) => {
  const isOnline: boolean = data?.[DatabaseProperty.DATABASE_ONLINE] ?? true;
  const reasoningSchemas: string[] =
    data?.[DatabaseProperty.REASONING_SCHEMAS] || [];
  const reasoningSchemaGraphs: string[] =
    data?.[DatabaseProperty.REASONING_SCHEMA_GRAPHS] || [];
  const sensitiveProperties =
    data?.[DatabaseProperty.SENSITIVE_PROPERTIES] || [];
  const sensitivePropertyGroups =
    formatSPGByName(data?.[DatabaseProperty.SENSITIVE_PROPERTY_GROUPS] || []) ||
    {};
  const defaultModelNamedGraphs = getDefaultModelNamedGraphs(
    reasoningSchemaGraphs
  );

  const { namedGraphsByModel, models } = getNamedGraphsByModel(
    reasoningSchemas,
    defaultModelNamedGraphs
  );

  return {
    isOnline,
    models,
    namedGraphsByModel,
    sensitiveProperties,
    sensitivePropertyGroups,
  };
};

// TODO: refactor reducer to fix slice types
type ActionsToFix =
  | UnknownAction<'connections/disconnected'>
  | UnknownAction<'connections/requestValidateConnection/fulfilled'>
  | UnknownAction<'connections/signedOut'>;

export const databasesReducer = (
  prevState: DatabasesState = new DatabasesState(),
  action: DatabasesAction | ActionsToFix
): DatabasesState => {
  switch (action.type) {
    case connectionsSlice.actions.disconnected.type:
    case connectionsSlice.actions.signedOut.type:
    case connectionAsyncActions.requestValidateConnection.fulfilled.type:
      return new DatabasesState();

    case DatabasesActionType.REFRESH_DATABASES_STARTED:
      return {
        ...prevState,
        pendingRefreshDatabases: true,
      };
    case DatabasesActionType.REFRESH_DATABASES_FINISHED:
      return {
        ...prevState,
        pendingRefreshDatabases: false,
      };

    case DatabasesActionType.REFRESH_DATABASE_STARTED: {
      const { databaseId } = action.payload;
      const database = prevState.databasesById[databaseId];
      if (!database) {
        return prevState;
      }

      return {
        ...prevState,
        databasesById: {
          ...prevState.databasesById,
          [databaseId]: {
            ...database,
            refreshed: false,
          },
        },
      };
    }
    case DatabasesActionType.REFRESH_DATABASE_FINISHED: {
      const { databaseId } = action.payload;
      const database = prevState.databasesById[databaseId];
      if (!database) {
        return prevState;
      }

      return {
        ...prevState,
        databasesById: {
          ...prevState.databasesById,
          [databaseId]: {
            ...database,
            refreshed: true,
          },
        },
      };
    }

    case DatabasesActionType.CREATE_DATABASE_SUCCESS: {
      const { databaseId } = action.payload.action;
      const databasesById: Record<string, Database> = {
        ...prevState.databasesById,
      };
      databasesById[databaseId] = {
        ...databasesById[databaseId],
        id: databaseId,
      };

      const databaseIds: string[] = [...prevState.databaseIds, databaseId];
      databaseIds.sort(alphaSorter);

      return {
        ...prevState,
        databasesById,
        databaseIds,
      };
    }

    case DatabasesActionType.REQUEST_DATABASES_SUCCESS: {
      const databaseIds: string[] = [
        ...action.payload?.response?.body?.databases,
      ];
      databaseIds.sort(alphaSorter);

      const databasesById = databaseIds.reduce<Record<string, Database>>(
        (acc, databaseId) => {
          acc[databaseId] = {
            id: databaseId,
            refreshed: false,
            isOnline: true,
          };
          return acc;
        },
        {}
      );

      return {
        ...prevState,
        databasesById,
        databaseIds,
      };
    }

    case DatabasesActionType.REQUEST_DATABASES_INFO_SUCCESS: {
      const databaseIds: string[] = [];
      const databasesById: Record<string, Database> = {};

      action.payload.response?.body?.databases?.forEach((data: any) => {
        const databaseId: string = data?.[DatabaseProperty.DATABASE_NAME] || '';
        if (!databaseId) {
          return;
        }

        databaseIds.push(databaseId);

        databasesById[databaseId] = {
          id: databaseId,
          refreshed: false,
          ...parseDatabaseInfo(data),
        };
      });

      databaseIds.sort(alphaSorter);

      return {
        ...prevState,
        databasesById,
        databaseIds,
      };
    }

    case DatabasesActionType.REQUEST_NAMED_GRAPHS_SUCCESS: {
      const { databaseId } = action.payload.action;
      const bindings = action.payload.response?.body?.results
        .bindings as NamedGraphBinding[];
      const databasesById: Record<string, Database> = {
        ...prevState.databasesById,
      };
      if (databasesById[databaseId]) {
        databasesById[databaseId] = {
          ...databasesById[databaseId],
          namedGraphs: bindings.reduce<Record<string, NamedGraph>>(
            (acc, binding) => ({
              ...acc,
              [binding.graph.value]: {
                iri: binding.graph.value,
                isAlias: binding.isAlias.value === 'true',
                isVirtual: binding.isVirtual.value === 'true',
              },
            }),
            {}
          ),
        };
      }

      return {
        ...prevState,
        databasesById,
      };
    }

    case DatabasesActionType.REQUEST_DATABASE_DETAILS_SUCCESS: {
      const { databaseId } = action.payload.action;

      const databasesById: Record<string, Database> = {
        ...prevState.databasesById,
      };
      if (databasesById[databaseId]) {
        databasesById[databaseId] = {
          ...databasesById[databaseId],
          id: databaseId,
          ...parseDatabaseInfo(action.payload.response?.body),
        };
      }

      return {
        ...prevState,
        databasesById,
      };
    }

    case DatabasesActionType.UPDATE_DATABASE_DETAILS_SUCCESS: {
      const { databaseId, details } = action.payload.action;

      const reasoningSchemas = details[DatabaseProperty.REASONING_SCHEMAS] as
        | string[]
        | undefined;
      if (!reasoningSchemas) {
        return prevState;
      }

      const databasesById: Record<string, Database> = {
        ...prevState.databasesById,
      };
      const database = databasesById[databaseId];
      if (database && database.namedGraphsByModel) {
        const { namedGraphsByModel, models } = getNamedGraphsByModel(
          reasoningSchemas,
          database.namedGraphsByModel[DEFAULT_MODEL]
        );

        databasesById[databaseId] = {
          ...database,
          id: databaseId,
          models,
          namedGraphsByModel,
        };
      }

      return {
        ...prevState,
        databasesById,
      };
    }

    case DatabasesActionType.REQUEST_DATABASE_NAMESPACES_SUCCESS: {
      const { databaseId } = action.payload.action;
      const namespaces = action.payload.response?.body;
      const prevStateDb = prevState.databasesById?.[databaseId];

      if (!namespaces || !prevStateDb) {
        return prevState;
      }

      return {
        ...prevState,
        databasesById: {
          ...prevState.databasesById,
          [databaseId]: {
            ...prevStateDb,
            id: databaseId,
            namespaces,
          },
        },
      };
    }

    case DatabasesActionType.IMPORT_DATABASE_NAMESPACES_SUCCESS: {
      const { databaseId } = action.payload.action;
      const namespaceList = action.payload.response?.body?.namespaces;
      const prevStateDb = prevState.databasesById?.[databaseId];

      if (!namespaceList || !prevStateDb) {
        return prevState;
      }

      const namespaces: Record<string, string> = {};
      namespaceList.forEach((keyValuePair: string) => {
        const firstEq = keyValuePair.indexOf('=');
        const key = keyValuePair.slice(0, firstEq);
        const value = keyValuePair.slice(firstEq + 1);
        namespaces[key] = value;
      });

      return {
        ...prevState,
        databasesById: {
          ...prevState.databasesById,
          [databaseId]: {
            ...prevStateDb,
            id: databaseId,
            namespaces,
          },
        },
      };
    }

    default:
      return prevState;
  }
};
